import { useEffect, useState } from 'react';
import { Button } from '../ui';
import {
  onAdsConsentChange,
  onCookieSettingsOpen,
  readAdsConsent,
  writeAdsConsent,
  type AdsConsent,
} from '../../lib/consent';
import { grantGoogleAds, startGoogleAds, stopGoogleAds } from '../../lib/googleAds';

export const COOKIE_POLICY_URL = 'https://seventide.app/cookie-policy/';

/**
 * Asks once (shared with the website) whether Google may set an advertising cookie to measure our adverts.
 * "Accept" and "Reject" are equally prominent. Google's tag loads in consent mode with advertising storage denied, so
 * Google sets no cookies until "Accept" (see lib/googleAds.ts). Re-opened by any
 * "Cookie settings" link (openCookieSettings). Hidden in tests unless `forceEnabled`.
 */
export function CookieBanner({ forceEnabled = false }: { forceEnabled?: boolean }) {
  const enabled = forceEnabled || import.meta.env.MODE !== 'test';
  const [open, setOpen] = useState(() => enabled && readAdsConsent() === null);

  useEffect(() => {
    if (!enabled) return;
    startGoogleAds();
    const offOpen = onCookieSettingsOpen(() => setOpen(true));
    const offChange = onAdsConsentChange((v) => (v === 'granted' ? grantGoogleAds() : stopGoogleAds()));
    return () => {
      offOpen();
      offChange();
    };
  }, [enabled]);

  if (!open) return null;
  const choose = (v: AdsConsent) => {
    writeAdsConsent(v);
    setOpen(false);
  };

  return (
    <section
      aria-label="Cookie choice"
      className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-border bg-surface p-4 shadow-lg sm:p-5"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3">
        <h2 className="text-lg font-bold">Can Google measure our adverts?</h2>
        <p className="text-base">
          If you agree, Google sets an advertising cookie so we can see which of our adverts bring people to Seventide.
          Google never sees your family&apos;s records or which pages you use, and we do not use it to show you adverts.
          You can change your mind at any time from &quot;Cookie settings&quot;.{' '}
          <a className="font-bold text-primary underline underline-offset-4" href={COOKIE_POLICY_URL}>
            Cookie policy
          </a>
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="secondary" onClick={() => choose('granted')}>
            Accept
          </Button>
          <Button variant="secondary" onClick={() => choose('denied')}>
            Reject
          </Button>
        </div>
      </div>
    </section>
  );
}
